import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  Eraser,
  Globe2,
  Info,
  RefreshCw,
  Search,
  ShieldCheck,
  X,
} from 'lucide-react';
import clsx from 'clsx';

import { adbApi, networkApi, type ProxyCandidate } from '@/lib/api';
import { KvCard } from '@/components/KvCard';
import { useNetwork } from '@/store/network';
import { CertPushModal } from './CertPushModal';

interface ProxyState {
  host?: string;
  port?: number;
  raw: string;
  enabled: boolean;
}

/**
 * Middle card. Three sub-sections:
 *   1. Currently-applied proxy (with one-tap "Clear").
 *   2. Auto-discovered candidates from the host machine (Charles, mitm, ...).
 *   3. Manual host/port entry + "Last 5" history (B1 prefs.global.proxy.mru，跟账号走).
 *
 * The CA-cert push button at the bottom opens {@link CertPushModal}.
 *
 * All mutations go through `adbApi.run`; we never write `settings` directly
 * from the browser. The card invalidates its own `net.proxy.get` query on
 * every successful set / clear so the "currently applied" header stays in
 * sync with reality.
 */
export function ProxyCard({ serial }: { serial?: string }) {
  const qc = useQueryClient();
  const enabled = !!serial;
  const { proxyHistory, rememberProxy, forgetProxy } = useNetwork();

  const proxyQ = useQuery({
    queryKey: ['net.proxy.get', serial],
    queryFn: () => adbApi.run<ProxyState>({ serial, name: 'net.proxy.get' }),
    enabled,
    staleTime: 5_000,
  });
  const candidatesQ = useQuery({
    queryKey: ['net.proxy.candidates'],
    queryFn: () => networkApi.proxyCandidates(),
    // Host-side scan is independent of `serial`. Cache for 30s so we don't
    // spam the LAN every time the user navigates back to this page.
    staleTime: 30_000,
  });

  const current = proxyQ.data?.result;
  const [host, setHost] = useState('');
  const [port, setPort] = useState('8888');
  const [busy, setBusy] = useState<'set' | 'clear' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [certOpen, setCertOpen] = useState(false);
  const [justApplied, setJustApplied] = useState<string | null>(null);

  // Pre-fill the form with the currently-applied proxy on first load so the
  // user can edit-in-place rather than retype everything.
  useEffect(() => {
    if (current?.host && !host) setHost(current.host);
    if (current?.port && port === '8888') setPort(String(current.port));
    // Intentionally only run when `current` first arrives (guarded by `!host`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.host, current?.port]);

  const refresh = (): void => {
    qc.invalidateQueries({ queryKey: ['net.proxy.get', serial] });
    qc.invalidateQueries({ queryKey: ['net.proxy.candidates'] });
  };

  const apply = async (h: string, p: number): Promise<void> => {
    if (!serial) return;
    setErr(null);
    setJustApplied(null);
    setBusy('set');
    try {
      await adbApi.run({ serial, name: 'net.proxy.set', args: { host: h, port: p } });
      rememberProxy(h, p);
      setJustApplied(`${h}:${p}`);
      refresh();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const clear = async (): Promise<void> => {
    if (!serial) return;
    setErr(null);
    setJustApplied(null);
    setBusy('clear');
    try {
      await adbApi.run({ serial, name: 'net.proxy.clear' });
      refresh();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const submit = (): void => {
    const p = parseInt(port, 10);
    if (!host.trim() || !Number.isFinite(p) || p < 1 || p > 65535) {
      setErr('host 不能为空，port 必须是 1-65535 的整数');
      return;
    }
    apply(host.trim(), p);
  };

  return (
    <>
      <KvCard
        title="HTTP 代理 · Proxy"
        icon={<Globe2 className="h-4 w-4 text-accent" />}
        action={
          <button
            className="btn-ghost"
            onClick={refresh}
            disabled={!enabled || proxyQ.isFetching || candidatesQ.isFetching}
            title="刷新"
          >
            <RefreshCw
              className={clsx('h-3.5 w-3.5', (proxyQ.isFetching || candidatesQ.isFetching) && 'animate-spin')}
            />
          </button>
        }
      >
        <div className="space-y-4">
          {/* ---------------- Currently applied ---------------- */}
          <section>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-ink-muted">
              <span>当前生效</span>
              <span className="badge font-normal">系统全局 · settings.global.http_proxy</span>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              {current?.enabled && current.host && current.port ? (
                <>
                  <code className="rounded bg-bg-soft px-2 py-1 font-mono text-sm text-ink">
                    {current.host}:{current.port}
                  </code>
                  <button
                    className="btn-ghost text-ink-muted"
                    disabled={!enabled || busy !== null}
                    onClick={clear}
                    title="清除代理"
                  >
                    <Eraser className="h-3.5 w-3.5" />
                    <span>清除</span>
                  </button>
                </>
              ) : (
                <span className="text-sm text-ink-muted">未设置（直连）</span>
              )}
            </div>
            {justApplied && (
              <div className="mt-2 flex items-start gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/5 px-3 py-2 text-[12px]">
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                <div className="min-w-0 text-ink-muted">
                  已写入 <code className="font-mono text-ink">{justApplied}</code>。
                  WebView / OkHttp 默认读全局代理；
                  <span className="font-medium text-ink">
                    手机「设置 → WiFi → 高级 → 代理」仍会显示「无」是预期
                  </span>
                  ，那是 per-network proxy（不同的字段，普通 adb 改不了）。
                </div>
              </div>
            )}
            <details className="mt-2 group">
              <summary className="flex cursor-pointer items-center gap-1 text-[11px] text-ink-muted hover:text-ink">
                <Info className="h-3 w-3" />
                为什么手机 WiFi 设置里看不到这个代理？
              </summary>
              <div className="mt-1.5 space-y-1 rounded-md border border-line bg-bg-soft/40 px-3 py-2 text-[12px] text-ink-muted">
                <p>
                  Android 上有 <strong className="text-ink">两套独立的代理</strong>：
                </p>
                <ul className="ml-3 list-disc space-y-0.5">
                  <li>
                    <span className="font-medium text-ink">系统全局</span>（这里设置的）：
                    <code className="font-mono">settings put global http_proxy</code>，
                    被 WebView、HttpURLConnection、OkHttp 默认 ProxySelector 等读取，
                    跨 WiFi / 移动数据 都生效。
                  </li>
                  <li>
                    <span className="font-medium text-ink">Per-network</span>（手机 UI 显示的）：
                    存在 <code className="font-mono">WifiConfiguration.ProxyInfo</code>，
                    随 WiFi 配置走。普通 adb 没有权限改它，需要 system signature。
                  </li>
                </ul>
                <p>
                  两套配置可同时存在，互不覆盖。所以这里写入后，App 抓包会通；但
                  「设置 → WiFi → 高级 → 代理」依旧显示「无」——这是 Android 设计如此，
                  并不意味着代理没生效。
                </p>
              </div>
            </details>
          </section>

          {/* ---------------- Auto candidates ---------------- */}
          <section>
            <div className="flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-wide text-ink-muted">
                <Search className="mr-1 inline h-3 w-3" />
                自动发现（主机 LAN 扫描）
              </div>
              {candidatesQ.isFetching && (
                <span className="text-[11px] text-ink-dim">扫描中...</span>
              )}
            </div>
            <div className="mt-1 space-y-1">
              {(candidatesQ.data?.candidates ?? []).length === 0 ? (
                <p className="text-[12px] text-ink-muted">
                  未发现 Charles / mitm / Whistle 的开放端口；可在下方手动输入。
                </p>
              ) : (
                (candidatesQ.data?.candidates ?? []).map((c) => (
                  <CandidateRow
                    key={`${c.host}:${c.port}`}
                    c={c}
                    disabled={!enabled || busy !== null}
                    onApply={() => apply(c.host, c.port)}
                  />
                ))
              )}
            </div>
          </section>

          {/* ---------------- Manual entry ---------------- */}
          <section>
            <div className="text-[11px] uppercase tracking-wide text-ink-muted">手动输入</div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <input
                className="input min-w-[180px] flex-1"
                placeholder="host (e.g. 192.168.1.10)"
                value={host}
                onChange={(e) => setHost(e.target.value)}
                disabled={!enabled || busy !== null}
              />
              <input
                className="input w-24"
                placeholder="port"
                inputMode="numeric"
                value={port}
                onChange={(e) => setPort(e.target.value.replace(/[^\d]/g, ''))}
                disabled={!enabled || busy !== null}
              />
              <button
                className="btn-primary"
                onClick={submit}
                disabled={!enabled || busy !== null}
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                应用
              </button>
            </div>
            {err && (
              <p className="mt-1 text-[12px] text-rose-500">
                {err}
                {/^UNSUPPORTED_CMD/.test(err) && (
                  <span className="ml-1 text-ink-muted">
                    （刚升级过 adh 请重新运行启动脚本，幂等替换会自动重启：
                    <code className="font-mono">yarn adh:restart</code>）
                  </span>
                )}
              </p>
            )}
          </section>

          {/* ---------------- History ---------------- */}
          {proxyHistory.length > 0 && (
            <section>
              <div className="text-[11px] uppercase tracking-wide text-ink-muted">最近 5 条</div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {proxyHistory.map((h) => (
                  <div
                    key={`${h.host}:${h.port}`}
                    className="flex items-center gap-1 rounded-full border border-line bg-bg-soft pl-2 pr-1 text-[12px]"
                  >
                    <button
                      className="font-mono text-ink-muted hover:text-accent"
                      disabled={!enabled || busy !== null}
                      onClick={() => apply(h.host, h.port)}
                      title="应用此代理"
                    >
                      {h.host}:{h.port}
                    </button>
                    <button
                      className="rounded-full p-0.5 text-ink-dim hover:bg-bg-card hover:text-rose-500"
                      onClick={() => forgetProxy(h.host, h.port)}
                      title="从历史中移除"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ---------------- CA cert push ---------------- */}
          <section className="border-t border-line pt-3">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-emerald-500" />
              <div className="flex-1">
                <div className="text-sm font-medium text-ink">推送 CA 证书</div>
                <div className="text-[11px] text-ink-muted">
                  抓 HTTPS 必须先安装 Charles / mitm 的根证书。Android 11+ 仅对配置了 networkSecurityConfig 的应用生效。
                </div>
              </div>
              <button
                className="btn"
                disabled={!enabled}
                onClick={() => setCertOpen(true)}
              >
                选择并推送
              </button>
            </div>
          </section>
        </div>
      </KvCard>

      {certOpen && (
        <CertPushModal
          serial={serial}
          onClose={() => setCertOpen(false)}
        />
      )}
    </>
  );
}

function CandidateRow({
  c,
  disabled,
  onApply,
}: {
  c: ProxyCandidate;
  disabled?: boolean;
  onApply: () => void;
}) {
  return (
    <div
      className={clsx(
        'flex items-center justify-between gap-2 rounded-md border px-2 py-1.5',
        c.alive
          ? 'border-emerald-500/40 bg-emerald-500/5'
          : 'border-line bg-bg-soft/40 opacity-70'
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        <span
          className={clsx(
            'h-2 w-2 shrink-0 rounded-full',
            c.alive ? 'bg-emerald-500' : 'bg-ink-dim'
          )}
          aria-label={c.alive ? 'alive' : 'dead'}
        />
        <code className="truncate font-mono text-sm">{c.host}:{c.port}</code>
        <span className="badge">{c.label}</span>
      </div>
      <button
        className={clsx('btn-ghost', !c.alive && 'opacity-60')}
        disabled={disabled}
        onClick={onApply}
        title={c.alive ? '应用此代理' : '应用（端口当前未响应，仍可强制写入）'}
      >
        应用
      </button>
    </div>
  );
}
